import produce from 'immer'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import {
	FilterBody,
	FilterFooter,
	FilterHeader,
	FilterWrapper,
} from '../../../FilterModal'
import { RenderAge } from '../RenderAge'
import { LinkLayover } from './LinkLayover'
import { PaxAlertRenderer } from './PaxAlertRenderer'
import { PaxCounter } from './PaxCounter'
import { PaxTab } from './PaxTab'

export const PaxDetailsWrapper = styled(FilterWrapper)`
	position: relative;
	${FilterHeader} + ${FilterBody} {
		top: 32px;
	}
`

const defaultRoomConfig = {
	adultCount: 0,
	childAges: [],
}

const ERROR_MESSAGE = {
	childAgeNotPresent: 'Please enter age details',
	infantCountInvalid: 'Infant count should not be greather than adult count',
	roomCapacityFull: 'You cannot add more than 6 people in a room',
	MaxPeopleAllowed: 'Maximum 9 people allowed in a trip',
}

const areAllChildInfant = childAges => {
	let allInfant = false
	childAges.forEach(age => {
		allInfant = age <= 2
	})
	return allInfant
}

/**
 * this function takes:
 * hotelRoomConfig : full hotel room config object
 * currentRoomDetails : current hotel room details
 * currentRoom : current room no.
 *  */
const checkRoomConfig = (hotelRoomConfig, currentRoomDetails, currentRoom) => {
	let newRoomError = {}

	// getting total adults from all rooms
	const totalAdults = hotelRoomConfig.reduce(
		(a, b) => a + (b['adultCount'] || 0),
		0,
	)

	// getting total childs from all rooms
	const totalChilds = hotelRoomConfig.reduce(
		(a, b) => a + (b['childAges']['length'] || 0),
		0,
	)

	if (totalAdults + totalChilds > 9) {
		newRoomError['all'] = {
			show: true,
			text: ERROR_MESSAGE.MaxPeopleAllowed,
			hideLink: true, // hideLink variable will ensure to not show +Add room button & disable it
		}
	} else if (
		//checking if in the current room has more infants than adults
		currentRoomDetails.adultCount <
			(currentRoomDetails.childAges || []).length &&
		areAllChildInfant(currentRoomDetails.childAges)
	) {
		newRoomError[currentRoom] = {
			show: true,
			text: ERROR_MESSAGE.infantCountInvalid,
			hideLink: true, // hideLink variable will ensure to not show +Add room button & disable it
		}
	} else if (
		//checking if in the current room no. of pax has exceeded limit of 6
		currentRoomDetails.adultCount +
			(currentRoomDetails.childAges || []).length >
		6
	) {
		newRoomError[currentRoom] = {
			show: true,
			text: ERROR_MESSAGE.roomCapacityFull,
		}
	} else {
		newRoomError[currentRoom] = { show: false }
	}

	return newRoomError
}

export const PaxDetails = ({
	hotelGuestRoomConfigurations = [defaultRoomConfig],
	onHotelRoomConfigUpdate,
	closeDropDown,
	trackerClass,
}) => {
	const [roomConfig, setRoomConfig] = useState(hotelGuestRoomConfigurations)
	const [currentRoom, setCurrentRoom] = useState(0)
	const [roomErrors, setRoomErrors] = useState(
		checkRoomConfig(roomConfig, roomConfig[currentRoom], currentRoom),
	)

	const currentRoomDetails = roomConfig[currentRoom] || defaultRoomConfig

	useEffect(() => {
		setRoomErrors(checkRoomConfig(roomConfig, currentRoomDetails, currentRoom))
	}, [hotelGuestRoomConfigurations])

	const addNewRoom = e => {
		e && e.preventDefault()
		const nextState = produce(roomConfig, draftState => {
			draftState.push({ adultCount: 2, childAges: [] })
		})

		const newError = checkRoomConfig(nextState, nextState[nextState.length - 1])
		if (newError['all']) {
			setRoomErrors(newError)
			return
		} else {
			setRoomConfig(nextState)
			setCurrentRoom(nextState.length - 1)
		}
	}

	const deleteRoom = e => {
		e && e.preventDefault()
		const nextState = produce(roomConfig, draftState => {
			draftState.splice(currentRoom, 1)
		})
		setRoomConfig(nextState)
		setCurrentRoom(nextState.length - 1)
		setRoomErrors(
			checkRoomConfig(
				nextState,
				nextState[nextState.length - 1],
				nextState.length - 1,
			),
		)
	}

	const isValidHotelConfig = roomConfig => {
		let isValid = true
		;(roomConfig.childAges || []).forEach(age => {
			if (isNaN(parseInt(age)) && !age) {
				isValid = false
			}
		})
		if (!isValid) {
			setRoomErrors({
				all: {
					show: true,
					text: ERROR_MESSAGE.childAgeNotPresent,
					hideLink: true, // hideLink variable will ensure to not show +Add room button & disable it
				},
			})
		}
		return isValid
	}

	const updateHotelRoomConfig = () => {
		let isValidConfig = true
		roomConfig.forEach(singleRoomConfig => {
			if (!isValidHotelConfig(singleRoomConfig)) {
				isValidConfig = false
			}
		})

		if (isValidConfig) {
			onHotelRoomConfigUpdate(roomConfig)
		}
	}

	const updateCurrentRoomDetails = (value, action) => {
		let newCurrentRoomState = currentRoomDetails

		switch (action) {
			case 'update_adult_count':
				newCurrentRoomState = produce(currentRoomDetails, draftState => {
					draftState.adultCount = value
				})
				break
			case 'increase_child':
				newCurrentRoomState = produce(currentRoomDetails, draftState => {
					if (draftState.childAges) {
						draftState.childAges.push(null)
					} else {
						draftState.childAges = [null]
					}
				})
				break
			case 'decrease_child':
				newCurrentRoomState = produce(currentRoomDetails, draftState => {
					draftState.childAges.pop()
				})
				break
		}

		const newState = produce(roomConfig, draftState => {
			draftState[currentRoom] = newCurrentRoomState
		})

		const roomError = checkRoomConfig(
			newState,
			newCurrentRoomState,
			currentRoom,
		)
		setRoomErrors(roomError)
		if (
			(roomError[currentRoom] && roomError[currentRoom].show) ||
			(roomError.all && roomError.all.show)
		) {
			return
		} else {
			setRoomConfig(newState)
		}
	}

	const updateChildAge = ({ age, childIndex }) => {
		let currentAges = [...currentRoomDetails.childAges]
		currentAges[childIndex] = age
		let newCurrentRoomDetails = {
			...currentRoomDetails,
			childAges: currentAges,
		}

		// if infant count is more than adult count, don't update the age.
		let errors = checkRoomConfig(roomConfig, newCurrentRoomDetails, currentRoom)
		if (
			errors[currentRoom].show &&
			errors[currentRoom].text === ERROR_MESSAGE.infantCountInvalid
		) {
			setRoomErrors(errors)
		} else {
			let newRoomConfig = produce(roomConfig, draftState => {
				draftState[currentRoom] = newCurrentRoomDetails
			})
			setRoomConfig(newRoomConfig)
		}
	}

	const currentRoomError = Object.keys(roomErrors['all'] || {}).length
		? roomErrors['all']
		: roomErrors[currentRoom] || {}

	return (
		<PaxDetailsWrapper className={trackerClass}>
			{currentRoomError.show ? (
				<FilterHeader>
					<PaxAlertRenderer
						links={
							currentRoomError.hideLink
								? []
								: [
										{
											text: '+ Add room',
											clickAction: addNewRoom,
										},
								  ]
						}
						alertContent={currentRoomError.text}
					/>
				</FilterHeader>
			) : null}
			<FilterBody bottom={'48px'} style={{ position: 'relative' }}>
				<PaxTab
					roomsCount={roomConfig.length}
					onClickAction={setCurrentRoom}
					activeIndex={currentRoom}
				/>
				<div>
					<PaxCounter
						count={currentRoomDetails.adultCount}
						suffixText={currentRoomDetails.adultCount > 1 ? 'Adults' : 'Adult'}
						incrementAction={value => {
							if (currentRoomError.show) {
								console.log({ currentRoomError })
								return
							}
							updateCurrentRoomDetails(value, 'update_adult_count')
						}}
						decrementAction={value =>
							updateCurrentRoomDetails(value, 'update_adult_count')
						}
						isIncrementDisable={
							currentRoomDetails.adultCount >= 6 || currentRoomError.show
						}
						isDecrementDisable={currentRoomDetails.adultCount < 2}
					/>
					<PaxCounter
						count={(currentRoomDetails.childAges || []).length}
						suffixText={
							(currentRoomDetails.childAges || []).length > 1
								? 'Children'
								: 'Child'
						}
						incrementAction={value => {
							if (currentRoomError.show) {
								return
							}
							updateCurrentRoomDetails(value, 'increase_child')
						}}
						decrementAction={value => {
							updateCurrentRoomDetails(value, 'decrease_child')
						}}
						isIncrementDisable={
							(currentRoomDetails.adultCount === 1 &&
								(currentRoomDetails.childAges || []).length > 2) ||
							(currentRoomDetails.adultCount >= 2 &&
								(currentRoomDetails.childAges || []).length > 3) ||
							currentRoomError.show
						}
						isDecrementDisable={
							(currentRoomDetails.childAges || []).length === 0
						}
					/>
					<RenderAge
						paxCount={currentRoomDetails.childAges || []}
						onSelectAction={updateChildAge}
					/>
				</div>

				<LinkLayover
					links={[
						{
							text: 'Delete room',
							iconClassName: 'pyt-minus-16',
							onClickAction: deleteRoom,
							isDisabled: roomConfig.length < 2,
							textColor: '#F15F75',
						},
						{
							text: 'Add room',
							iconClassName: 'pyt-plus-16',
							onClickAction: addNewRoom,
							isDisabled: roomConfig.length >= 6 || currentRoomError.hideLink,
							textColor: '#148099',
						},
					]}
				/>
			</FilterBody>
			<FilterFooter minHeight={'48px'} padding={'12px 16px'}>
				<div className="dd-ftr">
					<a
						className="pull-left bold anchor-link-grey"
						href=" "
						onClick={e => {
							e && e.preventDefault()
							closeDropDown()
						}}
					>
						Cancel
					</a>
					<a
						className="pull-right bold"
						href=" "
						onClick={e => {
							e && e.preventDefault()
							updateHotelRoomConfig()
						}}
					>
						Done
					</a>
				</div>
			</FilterFooter>
		</PaxDetailsWrapper>
	)
}
